/**
 * Стор аутентификации — управление токенами, авторизацией и magic link
 *
 * Основные функции:
 * - Вход через Telegram initData (goLogin)
 * - Вход через email magic link (goSignIn → validateMagicLink)
 * - Регистрация через email magic link (goSignUp → validateMagicLink)
 * - Обновление токенов (goRefreshToken)
 * - Персистентность токенов в localStorage (только web)
 */

// Фабрика Pinia сторов
import { defineStore } from 'pinia'
// Обёртка gRPC-вызовов с обработкой токенов и ошибок
import { grpcCall } from '@/grpc-client/grpc/grpc-call'
// Фабрика gRPC-клиентов для всех эндпоинтов
import { $grpcClient } from '@/grpc-client/grpc-client'
// Логгер событий с отправкой ошибок на бэкенд
import { EventLogger } from '@/utils/EventLogger'
// Стор пользователя — для записи user.id при авторизации
import { useUserStore } from '@/modules/user/stores/userStore'
// Стор API — для сброса серверов при логауте
import { useApiStore } from '@/modules/api/store/apiStore'
// Утилита проверки валидности JWT-токена
import { checkTokenValidation } from '@/utils/checkTokenValidation'
// Утилита парсинга isAdmin из JWT и записи в userStore
import { parseTokenAndSetAdmin } from '@/utils/parseTokenAndSetAdmin'
// Текст ошибки: бэкенд → иначе заглушка
import { grpcErrorMessage } from '@/utils/grpcErrorMessage'
import { tGlobal } from '@/plugins/i18n'
// Инъекция accessToken в gRPC-запросы
// Типизация payload для входа через Telegram
import type { IGoLoginPayload } from '@/modules/auth/types/go/IGoLoginPayload'
// Типизация payload для регистрации по email
import type { IGoSignUpPayload } from '@/modules/auth/types/go/IGoSignUpPayload'
// Типизация payload для входа по email
import type { IGoSignInPayload } from '@/modules/auth/types/go/IGoSignInPayload'
// Типизация payload для валидации magic link
import type { IValidateMagicLinkPayload } from '@/modules/auth/types/go/IValidateMagicLinkPayload'
// Типизация состояния стора
import type { IAuthStoreState } from '@/modules/auth/types/IAuthStoreState'

// Ключи localStorage для персистентности токенов (web only)
const LS_ACCESS_TOKEN = 'vpn_access_token'
// Ключ для refreshToken
const LS_REFRESH_TOKEN = 'vpn_refresh_token'

/**
 * Определяет, находимся ли мы в Telegram-контексте
 *
 * @returns {boolean} true если приложение открыто в Telegram WebApp
 */
const isInTelegramContext = (): boolean => {
  return !!(window as unknown as Record<string, unknown>).TelegramWebviewProxy || window !== window.parent
}

/**
 * Сохраняет токены в localStorage (только для web)
 *
 * @param {string} accessToken - JWT токен доступа
 * @param {string} refreshToken - JWT токен обновления
 */
const saveTokensToStorage = (accessToken: string, refreshToken: string): void => {
  if (isInTelegramContext()) return
  localStorage.setItem(LS_ACCESS_TOKEN, accessToken)
  localStorage.setItem(LS_REFRESH_TOKEN, refreshToken)
}

/**
 * Очищает токены из localStorage
 */
const clearTokensFromStorage = (): void => {
  localStorage.removeItem(LS_ACCESS_TOKEN)
  localStorage.removeItem(LS_REFRESH_TOKEN)
}

/**
 * Загружает токены из localStorage (только для web)
 *
 * @returns {{ accessToken: string, refreshToken: string }} Сохранённые токены
 */
const loadTokensFromStorage = (): { accessToken: string; refreshToken: string } => {
  if (isInTelegramContext()) return { accessToken: '', refreshToken: '' }
  return {
    accessToken: localStorage.getItem(LS_ACCESS_TOKEN) || '',
    refreshToken: localStorage.getItem(LS_REFRESH_TOKEN) || '',
  }
}

export const useAuthStore = defineStore('authStore', {
  state: (): IAuthStoreState => {
    // Загружаем токены из localStorage при инициализации (web only)
    const stored = loadTokensFromStorage()
    return {
      isAuth: false, // флаг авторизации пользователя
      accessToken: stored.accessToken, // токен доступа
      refreshToken: stored.refreshToken, // токен обновления
      isUserChecked: false, // флаг валидности токена
      isTelegramLoginAvailable: false, // telegram-login.js загружен (OIDC)
    }
  },
  getters: {},
  actions: {
    /**
     * Вход по готовым JWT (приложение открыло сайт с токенами в query).
     * @param accessToken — JWT доступа
     * @param refreshToken — JWT обновления
     * @returns true если токены записаны
     */
    async loginWithTokens(accessToken: string, refreshToken: string): Promise<boolean> {
      try {
        parseTokenAndSetAdmin(accessToken)
        this.accessToken = accessToken
        this.refreshToken = refreshToken
        saveTokensToStorage(accessToken, refreshToken)
        this.isAuth = true
        this.isUserChecked = true
        return true
      } catch (err) {
        console.error('loginWithTokens error:', err)
        return false
      }
    },

    /**
     * Выполняет вход через gRPC, передавая initData из Telegram WebApp
     * Сохраняет токены и устанавливает isAuth при успехе
     *
     * @param {IGoLoginPayload} payload - Данные для логина через Telegram
     * @returns Успешность и текст ошибки (с бэка, иначе заглушка)
     */
    async goLogin({ initData }: IGoLoginPayload): Promise<{ ok: boolean; error?: string }> {
      const userStore = useUserStore()
      console.log('goLogin запущен')
      const fallbackKey = 'errors.telegramLoginFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.authEndpoint.telegramLogin({ initData }, options),
          'telegramLogin',
        )
        if (ok && response.tokens && response.id) {
          if (!userStore.user) return { ok: false, error: tGlobal('errors.userInitFailed') }

          // 1. Сохраняем ID пользователя
          userStore.user.id = response.id
          const { accessToken, refreshToken } = response.tokens
          // 2. Парсим isAdmin из JWT
          parseTokenAndSetAdmin(accessToken)
          // 3. Записываем токены в state + localStorage
          this.accessToken = accessToken
          this.refreshToken = refreshToken
          saveTokensToStorage(accessToken, refreshToken)
          this.isAuth = true
          this.isUserChecked = true
          console.log('goLogin return true')
          return { ok: true }
        }
        if (response.error) {
          EventLogger.error('goLogin', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        } else {
          EventLogger.error('goLogin', [tGlobal(fallbackKey)])
        }

        return { ok: false, error: grpcErrorMessage(response.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('goLogin', [`Ошибка goLogin: ${(error as Error).message}`])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Выполняет вход через Telegram OIDC
     *
     * @param payload - Данные авторизации (idToken)
     * @returns Успешность входа и текст ошибки с бэка (если есть)
     */
    async telegramOIDCLogin(payload: { idToken: string }): Promise<{ ok: boolean; error?: string }> {
      const userStore = useUserStore()
      const fallbackKey = 'errors.telegramLoginFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.authEndpoint.telegramOIDCLogin(
              {
                idToken: payload.idToken,
              },
              options,
            ),
          'telegramOIDCLogin',
        )

        if (ok && response.tokens && response.id) {
          if (!userStore.user) return { ok: false, error: tGlobal('errors.userInitFailed') }

          userStore.user.id = response.id
          const { accessToken, refreshToken } = response.tokens
          parseTokenAndSetAdmin(accessToken)
          this.accessToken = accessToken
          this.refreshToken = refreshToken
          saveTokensToStorage(accessToken, refreshToken)
          this.isAuth = true
          this.isUserChecked = true
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('telegramOIDCLogin', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        }
        return { ok: false, error: grpcErrorMessage(response?.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('telegramOIDCLogin', [`Ошибка: ${(error as Error).message}`])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Привязывает Telegram-аккаунт через Telegram OIDC к текущему пользователю
     *
     * @param payload - Данные авторизации (idToken)
     * @returns Успешность привязки и текст ошибки
     */
    async linkTelegramOIDC(payload: { idToken: string }): Promise<{ ok: boolean; error?: string }> {
      const fallbackKey = 'errors.telegramLinkFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.linkTelegramOIDC(
              {
                accessToken: '', // Будет внедрено перехватчиком
                idToken: payload.idToken,
              },
              options,
            ),
          'linkTelegramOIDC',
        )

        if (ok) {
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('linkTelegramOIDC', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        }
        return { ok: false, error: grpcErrorMessage(response?.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('linkTelegramOIDC', [`Ошибка: ${(error as Error).message}`])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Обновляет пару JWT-токенов через refreshToken
     *
     * @returns Успешность обновления и текст ошибки
     */
    async goRefreshToken(): Promise<{ ok: boolean; error?: string }> {
      console.log('goRefreshToken запущен')
      const fallbackKey = 'errors.tokenRefreshFailed'
      const refreshToken = this.refreshToken || ''
      if (!refreshToken) {
        EventLogger.error('goRefreshToken', ['Нет refreshToken для обновления'])
        return { ok: false, error: tGlobal('errors.noRefreshToken') }
      }

      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.authEndpoint.refresh({ refreshToken }, options),
          'refresh',
        )
        console.log('goRefreshToken response:', response)
        if (ok && response.token) {
          const { accessToken, refreshToken } = response.token
          // Записываем токены в state + localStorage
          this.accessToken = accessToken
          this.refreshToken = refreshToken
          saveTokensToStorage(accessToken, refreshToken)
          this.isAuth = true
          this.isUserChecked = true
          return { ok: true }
        }
        if (response?.error) {
          EventLogger.error('goRefreshToken', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        } else {
          EventLogger.error('goRefreshToken', [tGlobal(fallbackKey)])
        }
        return { ok: false, error: grpcErrorMessage(response?.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('goRefreshToken', [`Ошибка goRefreshToken: ${(error as Error).message}`])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Выход из системы — очистка токенов, state и localStorage
     */
    async logout(reason = 'Ручной выход пользователя') {
      EventLogger.warn('authStore', `Выполняется logout. Причина: ${reason}`)
      const userStore = useUserStore()
      const apiStore = useApiStore()
      apiStore.$reset()
      this.isAuth = false
      this.isUserChecked = false
      this.accessToken = ''
      this.refreshToken = ''
      userStore.isUserData = false
      clearTokensFromStorage()
      // Реферальный код привязан к незавершённой регистрации — после выхода он не нужен,
      // иначе AuthPage снова покажет чип «Регистрация по реферальной ссылке»
      localStorage.removeItem('referral_code')
    },

    /**
     * Инициирует отправку magic link на email для входа (web)
     * НЕ сохраняет токены — только отправляет ссылку на почту
     *
     * @param {IGoSignInPayload} payload - Email для входа
     * @returns {Promise<{ ok: boolean; error?: string }>} ok=true → письмо отправлено
     */
    async goSignIn(payload: IGoSignInPayload): Promise<{ ok: boolean; error?: string }> {
      const fallbackKey = 'errors.signInFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.authEndpoint.signIn(
              { email: payload.email, domain: window.location.hostname },
              options,
            ),
          'signIn',
        )

        if (ok) {
          // Magic link отправлен на email, токены НЕ сохраняем
          return { ok: true }
        }

        if (response.error) {
          EventLogger.error('goSignIn', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        } else {
          EventLogger.error('goSignIn', [tGlobal(fallbackKey)])
        }
        return { ok: false, error: grpcErrorMessage(response.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('goSignIn', [`Ошибка goSignIn: ${(error as Error).message}`])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Инициирует регистрацию и отправку magic link на email (web)
     * НЕ сохраняет токены — только отправляет ссылку на почту
     *
     * @param {IGoSignUpPayload} payload - Данные для регистрации
     * @returns {Promise<{ ok: boolean; error?: string }>} ok=true → письмо отправлено
     */
    async goSignUp(payload: IGoSignUpPayload): Promise<{ ok: boolean; error?: string }> {
      const fallbackKey = 'errors.signUpFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.authEndpoint.signUp(
              {
                email: payload.email,
                domain: window.location.hostname,
                ...(payload.firstName ? { firstName: payload.firstName } : {}),
                ...(payload.lastName ? { lastName: payload.lastName } : {}),
                ...(payload.referrerCode ? { referrerCode: payload.referrerCode } : {}),
              },
              options,
            ),
          'signUp',
        )

        if (ok) {
          // Magic link отправлен на email, токены НЕ сохраняем
          return { ok: true }
        }

        if (response.error) {
          EventLogger.error('goSignUp', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        } else {
          EventLogger.error('goSignUp', [tGlobal(fallbackKey)])
        }
        return { ok: false, error: grpcErrorMessage(response.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('goSignUp', [`Ошибка goSignUp: ${(error as Error).message}`])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Валидирует magic link — обменивает userID+token на JWT-пару
     * Сохраняет токены и устанавливает isAuth при успехе
     *
     * @param {IValidateMagicLinkPayload} payload - userID и token из URL
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат валидации
     */
    async validateMagicLink(
      payload: IValidateMagicLinkPayload,
    ): Promise<{ ok: boolean; error?: string }> {
      const userStore = useUserStore()
      const fallbackKey = 'errors.magicLinkFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.authEndpoint.validateMagicLink(
              { email: payload.email, token: payload.token },
              options,
            ),
          'validateMagicLink',
        )

        if (ok && response.tokens && response.id) {
          if (!userStore.user)
            return { ok: false, error: tGlobal('errors.userInitFailed') }

          // 1. Сохраняем ID пользователя
          userStore.user.id = response.id
          const { accessToken, refreshToken } = response.tokens
          // 2. Парсим isAdmin из JWT
          parseTokenAndSetAdmin(accessToken)
          // 3. Записываем токены в state + localStorage
          this.accessToken = accessToken
          this.refreshToken = refreshToken
          saveTokensToStorage(accessToken, refreshToken)
          this.isAuth = true
          this.isUserChecked = true
          return { ok: true }
        }

        if (response.error) {
          EventLogger.error('validateMagicLink', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        } else {
          EventLogger.error('validateMagicLink', [tGlobal(fallbackKey)])
        }
        return { ok: false, error: grpcErrorMessage(response.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('validateMagicLink', [
          `Ошибка validateMagicLink: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Восстанавливает сессию из сохранённого accessToken
     * При невалидном accessToken пробует обновить через refreshToken
     *
     * @returns {Promise<boolean>} Успешность восстановления
     */
    async restoreAuth(): Promise<boolean> {
      console.warn('restoreAuth запущен')
      const accessToken = this.accessToken

      // 1. Если нет accessToken — пробуем обновить через refreshToken
      if (!accessToken) {
        console.warn('restoreAuth Нет токена в сторе')
        if (this.refreshToken) {
          console.warn('restoreAuth Пробуем обновить через refreshToken')
          const refreshed = await this.goRefreshToken()
          return refreshed.ok
        }
        return false
      }

      // 2. Проверяем валидность токена
      const isValid = await checkTokenValidation(accessToken, 'restoreAuth')
      if (isValid) {
        parseTokenAndSetAdmin(accessToken)
        this.isAuth = true
        this.isUserChecked = true
        console.warn('restoreAuth Сессия восстановлена')
        return true
      }

      // 3. Токен невалиден — пробуем refreshToken
      console.warn('restoreAuth Токен невалиден, пробуем refreshToken')
      if (this.refreshToken) {
        const refreshed = await this.goRefreshToken()
        if (refreshed.ok) return true
      }

      // 4. Не удалось восстановить — logout
      EventLogger.error('restoreAuth', ['Не удалось восстановить сессию'])
      await this.logout('Не удалось восстановить сессию при запуске приложения')
      return false
    },
  },
})
